import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Swal from 'sweetalert2';
import { getEmpresaByCodigo } from '../Componentes/empresasConfig';

// Función para formatear números como moneda chilena
const formatCurrency = (value) => {
    if (!value || value === 0) return "0";
    const numericValue = typeof value === 'string' ? parseFloat(value.replace(/[^0-9.-]/g, '')) : value;
    if (isNaN(numericValue)) return "0";
    return numericValue.toLocaleString('es-CL');
};

const getLogoPath = (selectedCard) => getEmpresaByCodigo(selectedCard).logoPDF;
const getEmpresaColor = (selectedCard) => getEmpresaByCodigo(selectedCard).color;
const getEmpresaRUT = (selectedCard) => getEmpresaByCodigo(selectedCard).rutFormatted;

// Nombre completo y firma por usuario (hardcodeado para no consultar la BD)
const FIRMANTES = {
    dfuentes: { nombre: 'Dionisio Fuentes Araneda', firma: '/img/fgd78fgd12.png' },
    jgarcias: { nombre: 'Jaritza Garcias Riffo', firma: '/img/d34rdf2hi56ocz.png' },
};
const FIRMANTE_POR_DEFECTO = 'dfuentes';

const getFirmante = (usuario) => FIRMANTES[String(usuario || '').toLowerCase().trim()];

export const generarOrdenCompraPDF = async (ordenData) => {
    try {
        console.log('Iniciando generación de PDF con datos:', ordenData);

        const {
            numeroOrden,
            fechaOrden,
            senores,
            rutProveedor,
            comuna,
            ciudad,
            direccion,
            telefono,
            emisorNombre,
            emisorRut,
            usuarioAprueba, // Usuario que aprueba la orden (define la firma del VºBº)
            prioridad,
            tipoTrabajo,
            detalles,
            subtotal,
            iva,
            total,
            empresa,
            selectedCard,
            observaciones
        } = ordenData;

        // Validar datos esenciales
        if (!numeroOrden) {
            throw new Error('Número de orden es requerido');
        }

        if (!senores) {
            throw new Error('Información del proveedor es requerida');
        }

        const doc = new jsPDF();
        const primaryColor = getEmpresaColor(selectedCard);

        // Configurar colores
        doc.setTextColor(0, 0, 0);

        // Cargar imágenes necesarias con Promise.all
        const cargarImagenes = async () => {
            const promises = [];

            // Cargar logo
            const logoPromise = new Promise((resolve) => {
                const logoPath = getLogoPath(selectedCard);
                const logoImg = new Image();
                logoImg.onload = () => resolve(logoImg);
                logoImg.onerror = () => {
                    console.warn('No se pudo cargar el logo');
                    resolve(null);
                };
                logoImg.src = logoPath;
            });
            promises.push(logoPromise);

            // Cargar firma del usuario que aprueba (dfuentes por defecto)
            const firmaPromise = new Promise((resolve) => {
                const firmante = getFirmante(usuarioAprueba) || FIRMANTES[FIRMANTE_POR_DEFECTO];
                const firmaImg = new Image();
                firmaImg.onload = () => resolve(firmaImg);
                firmaImg.onerror = () => {
                    console.warn('No se pudo cargar la firma:', firmante.firma);
                    resolve(null);
                };
                firmaImg.src = firmante.firma;
            });
            promises.push(firmaPromise);

            return Promise.all(promises);
        };

        // Esperar a que se carguen todas las imágenes
        const [logoData, firmaData] = await cargarImagenes();

        // Continuar con la generación del PDF
        // Agregar logo si se cargó exitosamente
        if (logoData) {
            doc.addImage(logoData, 'PNG', 15, 10, 45, 15);
        }

        // Encabezado principal - logo y empresa más pequeños
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
        doc.text(empresa, 15, 32);

        // RUT de la empresa debajo del nombre
        doc.setFontSize(9);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(100, 100, 100);
        doc.text('RUT: ' + getEmpresaRUT(selectedCard), 15, 37);

        // Número de orden y fecha (lado derecho) - más compacto
        doc.setFontSize(12);
        doc.setTextColor(0, 0, 0);
        doc.rect(140, 10, 55, 20);
        doc.setFont(undefined, 'bold');
        doc.text('N° ' + numeroOrden, 142, 18);
        doc.setFontSize(9);
        doc.setFont(undefined, 'normal');
        doc.text('FECHA: ' + new Date(fechaOrden).toLocaleDateString('es-CL'), 142, 25);

        // Título del documento más compacto
        doc.setFontSize(16);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
        doc.text('ORDEN DE SERVICIO', 105, 48, { align: 'center' });

        // Información del proveedor más compacta
        let yPos = 58;
        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text('Señor(es):', 15, yPos);
        doc.text('RUT:', 120, yPos);

        doc.setFont(undefined, 'normal');
        doc.text(senores, 40, yPos);
        doc.text(rutProveedor, 135, yPos);

        yPos += 7;
        doc.setFont(undefined, 'bold');
        doc.text('Dirección:', 15, yPos);
        doc.text('Comuna:', 120, yPos);

        doc.setFont(undefined, 'normal');
        doc.text(direccion, 40, yPos);
        doc.text(comuna, 140, yPos);

        yPos += 7;
        doc.setFont(undefined, 'bold');
        doc.text('Ciudad:', 15, yPos);
        doc.text('Fono:', 120, yPos);

        doc.setFont(undefined, 'normal');
        doc.text(ciudad, 35, yPos);
        doc.text(telefono || 'N/A', 135, yPos);

        // Sección de identificación más compacta
        yPos += 12;
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text('IDENTIFICACIÓN', 15, yPos);

        yPos += 5;
        doc.rect(15, yPos, 180, 18);

        yPos += 6;
        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        doc.text('Emisor:', 20, yPos);
        doc.text('RUT:', 20, yPos + 5);
        doc.text('Prioridad:', 105, yPos);
        doc.text('Tipo de Trabajo:', 105, yPos + 5);

        doc.setFont(undefined, 'normal');
        doc.text(emisorNombre, 35, yPos);
        doc.text(emisorRut, 35, yPos + 5);
        doc.text(prioridad, 130, yPos);
        doc.text(tipoTrabajo, 145, yPos + 5);

        // Descripción del trabajo más compacta
        yPos += 25;
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text('DESCRIPCIÓN DEL TRABAJO', 15, yPos);

        yPos += 5;

        // Tabla de detalles
        const tableColumns = ['N', 'Descripción', 'Valor Unit.', 'Cant.', 'Subtotal', 'IVA', 'Valor Total'];

        // Verificar que detalles sea un array y tenga elementos
        let detallesArray = Array.isArray(detalles) ? detalles : [];

        // Si no hay detalles, crear al menos una fila vacía para mostrar la estructura
        if (detallesArray.length === 0) {
            detallesArray = [{
                Item: 1,
                Descripcion: 'Sin detalles especificados',
                Valor_Unitario: 0,
                Cantidad: 1
            }];
        }

        const tableRows = detallesArray.map((detalle, index) => {
            // Validar que el detalle sea un objeto
            if (!detalle || typeof detalle !== 'object') {
                return [
                    index + 1,
                    'Error en detalle',
                    '$0',
                    1,
                    '$0',
                    '$0',
                    '$0'
                ];
            }

            // Obtener valores, priorizando los nombres de la DB y validando tipos
            const valorUnitario = parseFloat(detalle.Valor_Unitario || detalle.valorUnitario || 0) || 0;
            const cantidad = parseInt(detalle.Cantidad || detalle.cantidad || 1) || 1;
            const descripcion = (detalle.Descripcion || detalle.descripcion || '').toString().trim();

            // Validar que la descripción no esté vacía
            const descripcionFinal = descripcion || `Detalle ${index + 1}`;

            // Calcular subtotal e IVA con validaciones
            const subtotal = Math.round(valorUnitario * cantidad * 100) / 100; // Redondear a 2 decimales
            const iva = Math.round(subtotal * 0.19 * 100) / 100; // Redondear a 2 decimales
            const valorTotal = Math.round((subtotal + iva) * 100) / 100; // Redondear a 2 decimales

            return [
                detalle.Item || detalle.item || (index + 1),
                descripcionFinal,
                '$' + formatCurrency(valorUnitario),
                cantidad,
                '$' + formatCurrency(subtotal),
                '$' + formatCurrency(iva),
                '$' + formatCurrency(valorTotal)
            ];
        });

        // Verificación final: si no hay filas, agregar una fila de placeholder
        if (tableRows.length === 0) {
            tableRows.push([
                1,
                'Sin información de detalles disponible',
                '$0',
                1,
                '$0',
                '$0',
                '$0'
            ]);
        }

        try {
            doc.autoTable({
                startY: yPos,
                head: [tableColumns],
                body: tableRows,
                theme: 'grid',
                headStyles: {
                    fillColor: primaryColor,
                    textColor: [255, 255, 255],
                    fontStyle: 'bold',
                    fontSize: 8,
                    cellPadding: 2
                },
                bodyStyles: {
                    fontSize: 8,
                    cellPadding: 2,
                    lineWidth: 0.1
                },
                columnStyles: {
                    0: { cellWidth: 10, halign: 'center' },
                    1: { cellWidth: 70 },
                    2: { cellWidth: 18, halign: 'right' },
                    3: { cellWidth: 10, halign: 'center' },
                    4: { cellWidth: 18, halign: 'right' },
                    5: { cellWidth: 18, halign: 'right' },
                    6: { cellWidth: 26, halign: 'right' }
                },
                margin: { left: 15, right: 15 },
                styles: {
                    lineColor: [200, 200, 200],
                    lineWidth: 0.1
                }
            });
        } catch (tableError) {
            console.error('Error al generar la tabla:', tableError);

            // Si falla la tabla, generar una tabla simple alternativa
            yPos += 10;
            doc.setFontSize(10);
            doc.setFont(undefined, 'normal');
            doc.text('Error al cargar detalles de la orden', 15, yPos);
            doc.text('Por favor, revise la información en el sistema', 15, yPos + 5);
            yPos += 15;
        }

        // Totales más compactos
        // Verificar si la tabla se generó correctamente
        if (doc.lastAutoTable && doc.lastAutoTable.finalY) {
            yPos = doc.lastAutoTable.finalY + 8;
        } else {
            // Si no hay tabla o falló, usar la posición actual + espacio
            yPos += 10;
        }

        // Crear rectángulo para totales
        doc.rect(135, yPos - 5, 60, 20);

        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.text('NETO', 140, yPos + 2);
        doc.text('IVA', 140, yPos + 7);
        doc.text('TOTAL', 140, yPos + 12);

        doc.setFont(undefined, 'normal');
        doc.text('$' + formatCurrency(subtotal), 170, yPos + 2);
        doc.text('$' + formatCurrency(iva), 170, yPos + 7);

        doc.setFont(undefined, 'bold');
        doc.text('$' + formatCurrency(total), 170, yPos + 12);

        // Observaciones más compactas si existen
        if (observaciones && observaciones.trim()) {
            yPos += 15;
            doc.setFontSize(10);
            doc.setFont(undefined, 'bold');
            doc.text('OBSERVACIONES:', 15, yPos);

            yPos += 5;
            doc.setFont(undefined, 'normal');
            doc.setFontSize(9);

            const observacionesLines = doc.splitTextToSize(observaciones, 180);
            doc.text(observacionesLines, 15, yPos);
            yPos += observacionesLines.length * 4;
        }

        // Sección de firmas VºBº - Solo 3 cuadrados
        yPos += 12;
        if (yPos > 230) {
            doc.addPage();
            yPos = 20;
        }

        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.text('VºBº', 15, yPos);

        yPos += 5;

        // Crear tabla de firmas - solo 3 cuadrados más grandes
        const firmaWidth = 50;
        const firmaHeight = 30;
        const startX = 20;
        const spacing = 15;

        const firmasData = [
            { titulo: 'EMISOR', nombre: getFirmante(emisorNombre)?.nombre || emisorNombre },
            { titulo: 'VºBº ADMINISTRADOR', conFirma: true },
            { titulo: 'RECEPCIÓN CONFORME' }
        ];

        // Dibujar las 3 celdas de firmas horizontalmente
        for (let i = 0; i < firmasData.length; i++) {
            const x = startX + (i * (firmaWidth + spacing));
            const y = yPos;

            // Dibujar rectángulo
            doc.rect(x, y, firmaWidth, firmaHeight);

            // Título en la parte inferior del cuadrado
            doc.setFontSize(7);
            doc.setFont(undefined, 'bold');
            doc.text(firmasData[i].titulo, x + (firmaWidth / 2), y + firmaHeight - 3, { align: 'center' });

            // Si es el EMISOR, agregar el nombre del usuario
            if (i === 0 && firmasData[i].nombre) {
                doc.setFontSize(8);
                doc.setFont(undefined, 'normal');
                doc.text(firmasData[i].nombre, x + (firmaWidth / 2), y + 10, { align: 'center' });
            }

            // Si es VºBº ADMINISTRADOR, agregar la firma si se cargó
            if (i === 1 && firmasData[i].conFirma) {
                if (firmaData) {
                    console.log('Agregando firma del administrador al PDF');
                    // Ajustar la firma dentro del cuadrado
                    const firmaX = x + 5;
                    const firmaY = y + 3;
                    const firmaW = firmaWidth - 10;
                    const firmaH = firmaHeight - 15;
                    doc.addImage(firmaData, 'PNG', firmaX, firmaY, firmaW, firmaH);
                } else {
                    console.warn('firmaData es null - no se pudo cargar la imagen de la firma');
                }
            }
        }

        // Pie de página con información de la empresa más compacto
        doc.setFontSize(7);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(120, 120, 120);
        const pageHeight = doc.internal.pageSize.height;
        doc.text(empresa, 15, pageHeight - 8);
        doc.text('Generado: ' + new Date().toLocaleDateString('es-CL'), 130, pageHeight - 8);

        // Guardar el PDF
        const fileName = `Orden_Compra_${numeroOrden}_${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(fileName);

    } catch (error) {
        console.error('Error al generar PDF:', error);
        Swal.fire({
            icon: 'error',
            title: 'Error al generar PDF',
            text: 'No se pudo generar el PDF. Por favor, inténtelo nuevamente.',
            confirmButtonColor: '#d33'
        });
    }
};